import { Button as FlexoButton } from '@flexo/atoms';
import { RIGHT_DRAWER, store } from '@flexo/reducers';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { BaseHeaderWeb } from './components/BaseHeaderWeb';
import styles from './header-web.module.scss';
import { useBreadcrumbs, useHeaderSelect } from './hooks';
import { getHeaderConfig } from './utils/headerConfig';

import { HiveCalendar } from '../hive-calendar/hive-calendar';
import './header-web.module.scss';

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const { Header__Web, Header__Web__Safari, Navigation } = styles;

function SimpleNavigation(props: { label?: string }) {
  const { t } = useTranslation();

  return (
    <div className={Navigation}>
      <strong>{t(`widgets.header.${props.label}`)}</strong>
    </div>
  );
}

function HeaderWeb(props: Record<string, unknown>) {
  const { t } = useTranslation();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const config = getHeaderConfig(pathSegment, props);
  const dispatch = store.dispatch as (action: unknown) => unknown;
  const isAdminHeader = config.type === 'headerWebAdmin';
  // FLEXO-4604: dedicated modifier for the community overview/energy/economics
  // pages (checked on the path, not config.type, which is also the default
  // fallback).
  const isCommunityPagesHeader =
    pathSegment === 'community' ||
    pathSegment === 'community-energy' ||
    pathSegment === 'community-economics';

  // Admin tables loading state
  const activeStatus = useSelector(
    (state: any) => state.metadata?.AdminsActiveAsyncStatus
  );
  const activePagination = useSelector(
    (state: any) => state.metadata?.adminsActivePagination
  );
  const invitedStatus = useSelector(
    (state: any) => state.metadata?.AdminsInvitedAsyncStatus
  );
  const invitedPagination = useSelector(
    (state: any) => state.metadata?.adminsInvitedPagination
  );

  const activeStillFetching =
    activeStatus === 'PENDING' ||
    activeStatus === 'LOADING_MORE' ||
    (activePagination &&
      activePagination.page < (activePagination.totalPages || 1));
  const invitedStillFetching =
    invitedStatus === 'PENDING' ||
    invitedStatus === 'LOADING_MORE' ||
    (invitedPagination &&
      invitedPagination.page < (invitedPagination.totalPages || 1));
  const isAdminDataLoading =
    isAdminHeader && (activeStillFetching || invitedStillFetching);

  const breadcrumbs = useBreadcrumbs(config.type);
  const {
    selectOptions,
    currentSelectValue,
    onSelectChange,
    isSelectOpen,
    setSelectOpen,
    triggerSelectOpen,
  } = useHeaderSelect(config.type);

  // Handle default case - if no specific config, show simple navigation
  if (config.type === 'default') {
    return (
      <div
        className={`${Header__Web} ${isSafari ? 'Header__Web__Safari' : ''}`}
      >
        <SimpleNavigation {...props} />
      </div>
    );
  }

  return (
    <>
      <div
        className={[
          Header__Web,
          isSafari ? Header__Web__Safari : '',
          isAdminHeader ? styles['Header__Web--admin'] : '',
          isCommunityPagesHeader
            ? styles['Header__Web--community-pages']
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <BaseHeaderWeb
          allowUserCreation={config?.allowUserCreation}
          breadcrumbs={breadcrumbs}
          showCalendar={(typeof config.showCalendar === 'boolean' && config.showCalendar === true)}
          calendarHidden={config.calendarHidden}
          showSearch={config.showSearch}
          showProfile={config.showProfile}
          showDownload={config.showDownload}
          selectOptions={selectOptions}
          currentSelectValue={currentSelectValue}
          onSelectChange={onSelectChange}
          isSelectOpen={isSelectOpen}
          setSelectOpen={setSelectOpen}
          triggerSelectOpen={triggerSelectOpen}
        />

        {config.showCalendar && (
          <div className={styles.HiveCalendar__Small}>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <HiveCalendar />
          </div>
        )}

        {config.calendarHidden && (
          <div className={styles.HiveCalendar__Small} style={{ pointerEvents: 'none' }}>
            <HiveCalendar hideInput />
          </div>
        )}
      </div>

      {config?.allowUserCreation && (
        <div className={styles.Header__Web__AllowUserCreationBar}>
          <FlexoButton
            size="micro"
            variant="primary"
            color="secondary"
            className={styles.Header__Web__AllowUserCreationBar__Button}
            label={t('adminUsers.addNewUser', { defaultValue: 'Add new user' })}
            onClick={() => {
              const drawerPayload: { type: string; show: boolean } = {
                type: 'ADMIN_ADD_NEW_USER',
                show: true,
              };
              const setDrawer = RIGHT_DRAWER.SetDrawer as unknown as (
                payload: unknown
              ) => unknown;
              dispatch(setDrawer(drawerPayload));
            }}
          />
        </div>
      )}
      {isAdminDataLoading && (
        <CircularProgress
          size={30}
          thickness={5}
          sx={{
            position: 'absolute',
            top: '100vh',
            left: 40,
            zIndex: 1000,
          }}
        />
      )}
    </>
  );
}

export default React.memo(HeaderWeb);
